/* eslint-disable camelcase */
import React, { useCallback, useEffect } from 'react';

import { Helmet } from 'react-helmet';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { OrganizationSelectors } from '../../../Redux/modules/organization/selectors';
import styles from './OrganizationContainer.module.less';
import OrganizationDetails from '../components/OrganizationDetails';
import OrganizationDetailsEdit from '../components/OrganizationDetailsEdit';
import Administrators from '../components/Administrators';
import SsoSettings from '../components/SsoSettings';
import { OrganizationActions } from '../../../Redux/modules/organization/actions';
import { AppActions } from '../../../Redux/modules/app/actions';
import { AppSelectors } from '../../../Redux/modules/app/selectors';
import { UsersSelectors } from '../../../Redux/modules/users/selectors';
import IPWhitelist from '../components/IPWhitelist';

const organizationContainer = (props) => {
  const isForcedGoogleLogin = Boolean(props.organizationDetails?.enforce_google_auth);
  const isForcedAmazonLogin = Boolean(props.organizationDetails?.enforce_amazon_auth);
  const isForcedOidcLogin = Boolean(props.organizationDetails?.enforce_oidc_auth);

  const administrators = props.users.filter((user) => user.is_org_admin);
  useEffect(() => {
    props.onShowOrganization();
    props.getUseCases();
  }, []);

  const onButtonClick = useCallback(() => {
    props.setEditMode(!props.editMode);
  }, []);

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{gettext('Organization')}</title>
      </Helmet>
      <div className={styles.section}>
        <div>
          {props.organizationDetails ? (
            <div>
              {!props.isLoadingOrganization && props.editMode ? (
                <OrganizationDetailsEdit
                  organizationDetails={props.organizationDetails}
                  onOrganizationChange={props.onEditDetails}
                  onButtonClick={onButtonClick}
                  useCasesOptions={props.useCasesOptions}
                  organizationDetailsUpdateStatus={props.organizationDetailsUpdateStatus}
                  updateError={props.updateError}
                  isLoading={props.isLoading}
                />
              ) : (
                <OrganizationDetails
                  isLoading={props.isLoadingOrganization}
                  organizationDetails={props.organizationDetails}
                  onButtonClick={onButtonClick}
                />
              )}
            </div>
          ) : null}
          <Administrators isLoading={props.isLoadingOrganization} administrators={administrators} />
          <SsoSettings
            googleStatus={isForcedGoogleLogin}
            amazonStatus={isForcedAmazonLogin}
            oidcStatus={isForcedOidcLogin}
            enableGoogleLogin={props.onEnableForcedGoogle}
            enableAmazonLogin={props.onEnableForcedAmazon}
            enableOidcLogin={props.onEnableForcedOidc}
            disableGoogleLogin={props.onDisableForcedGoogle}
            disableAmazonLogin={props.onDisableForcedAmazon}
            disableOidcLogin={props.onDisableForcedOidc}
            googleLoginUpdateStatus={props.googleLoginUpdateStatus}
            amazonLoginUpdateStatus={props.amazonLoginUpdateStatus}
            oidcLoginUpdateStatus={props.oidcLoginUpdateStatus}
            isAbleToEnforceGoogleLogin={props.isAbleToEnforceGoogleLogin}
            isAbleToEnforceAmazonLogin={props.isAbleToEnforceAmazonLogin}
            isAbleToEnforceOidcLogin={props.isAbleToEnforceOidcLogin}
          />
          <IPWhitelist />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    isLoadingOrganization: OrganizationSelectors.isLoadingOrganizationSelector(state),
    editMode: OrganizationSelectors.organizationDetailsEditModeSelector(state),
    organizationDetails: OrganizationSelectors.organizationDetailsSelector(state),
    users: UsersSelectors.orgUsersSelector(state),
    useCasesOptions: AppSelectors.useCasesSelector(state),
    organizationDetailsUpdateStatus: OrganizationSelectors.organizationDetailsUpdateStatusSelector(state),
    updateError: OrganizationSelectors.updateError(state),
    googleLoginUpdateStatus: OrganizationSelectors.googleLoginUpdateStatusSelector(state),
    amazonLoginUpdateStatus: OrganizationSelectors.amazonLoginUpdateStatusSelector(state),
    oidcLoginUpdateStatus: OrganizationSelectors.oidcLoginUpdateStatusSelector(state),
    isLoading: OrganizationSelectors.editingOrganizationLoaderSelector(state),
    isAbleToEnforceGoogleLogin: OrganizationSelectors.ableToEnforceGoogleSelector(state),
    isAbleToEnforceAmazonLogin: OrganizationSelectors.ableToEnforceAmazonSelector(state),
    isAbleToEnforceOidcLogin: OrganizationSelectors.ableToEnforceOidcSelector(state),
  };
};

const mapDispatchToProps = {
  setEditMode: OrganizationActions.toggleOrganizationEditMode,
  onShowOrganization: OrganizationActions.organizationDetailsFetch,
  onEditDetails: OrganizationActions.editDetailsHandler,
  onEnableForcedGoogle: OrganizationActions.forceGoogleLoginEnable,
  onEnableForcedAmazon: OrganizationActions.forceAmazonLoginEnable,
  onEnableForcedOidc: OrganizationActions.forceOidcLoginEnable,
  onDisableForcedGoogle: OrganizationActions.forceGoogleLoginDisable,
  onDisableForcedAmazon: OrganizationActions.forceAmazonLoginDisable,
  onDisableForcedOidc: OrganizationActions.forceOidcLoginDisable,
  getUseCases: AppActions.fetchUseCases,
  fetchIPWhitelist: OrganizationActions.ipWhitelistFetch,
};

organizationContainer.propTypes = {
  isLoadingOrganization: PropTypes.bool,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  organizationIpWhitelist: PropTypes.object,
  organizationDetails: PropTypes.object,
  onShowOrganization: PropTypes.func,
  users: PropTypes.array,
  onEnableForcedGoogle: PropTypes.func,
  onEnableForcedAmazon: PropTypes.func,
  onEnableForcedOidc: PropTypes.func,
  onDisableForcedGoogle: PropTypes.func,
  onDisableForcedAmazon: PropTypes.func,
  onEditDetails: PropTypes.func,
  getUseCases: PropTypes.func,
  useCasesOptions: PropTypes.array,
  isForcedGoogleLogin: PropTypes.bool,
  isLoading: PropTypes.bool,
  organizationDetailsUpdateStatus: PropTypes.bool,
  googleLoginUpdateStatus: PropTypes.bool,
  amazonLoginUpdateStatus: PropTypes.bool,
  oidcLoginUpdateStatus: PropTypes.bool,
  updateError: PropTypes.object,
  isAbleToEnforceGoogleLogin: PropTypes.bool,
  isAbleToEnforceAmazonLogin: PropTypes.bool,
  isAbleToEnforceOidcLogin: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(organizationContainer);
